import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Grid, Alert, Button,
} from '@mui/material';
import {
    Report as ReportIcon,
    PendingActions as PendingIcon,
    CheckCircle as ResolvedIcon,
    Person as UserIcon,
    Schedule as ScheduleIcon,
    Block as BlockIcon,
    Gavel as GavelIcon,
    PersonAdd as PersonAddIcon,
    Favorite as MatchIcon,
    Whatshot as TodayMatchIcon,
    FavoriteBorder as KeepRateIcon,
    TouchApp as DecisionsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminApi, DashboardStats, MatchmakingAnalytics } from '../../api/adminApi';
import { supabase } from '../../api/supabase';
import AdminSidebar from './components/AdminSidebar';
import KPIStatCard from './components/KPIStatCard';
import TrendChart from './components/TrendChart';
import ActivityFeed, { ActivityItem } from './components/ActivityFeed';
import NeedsAttentionPanel, { AttentionItem } from './components/NeedsAttentionPanel';
import { adminTokens as T } from '../../theme/adminTokens';

const DRAWER_WIDTH = 260;

const formatReportType = (type: string) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const AdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [signupTrend, setSignupTrend] = useState<Array<{ date: string; signups: number }>>([]);
    const [signupSummary, setSignupSummary] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [trendLoading, setTrendLoading] = useState(true);
    const [matchmaking, setMatchmaking] = useState<MatchmakingAnalytics | null>(null);
    const [matchmakingLoading, setMatchmakingLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchDashboardStats();
        fetchSignupData();
        fetchMatchmaking();

        const subscription = supabase
            .channel('admin-dashboard')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'user_reports' }, () => {
                fetchDashboardStats();
            })
            .subscribe();

        return () => { supabase.removeChannel(subscription); };
    }, []);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await adminApi.getDashboardStats();
            setStats(response.data);
        } catch (err: any) {
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchSignupData = async () => {
        try {
            setTrendLoading(true);
            const [summary, trend] = await Promise.all([
                adminApi.getSignupStats().catch(() => null),
                adminApi.getSignupTrend({ days: 30 }).catch(() => null),
            ]);
            setSignupSummary(summary?.data ?? null);
            const trendData = Array.isArray(trend?.data) ? trend!.data : (trend?.data?.trend ?? []);
            setSignupTrend(
                trendData.map((row: any) => ({
                    date: new Date(row.date ?? row.day ?? row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                    signups: Number(row.signups ?? row.count ?? row.total ?? 0),
                }))
            );
        } catch {
            setSignupTrend([]);
        } finally {
            setTrendLoading(false);
        }
    };

    const fetchMatchmaking = async () => {
        try {
            setMatchmakingLoading(true);
            const r = await adminApi.getMatchmakingAnalytics({ days: 30 });
            setMatchmaking(r.data ?? null);
        } catch {
            setMatchmaking(null);
        } finally {
            setMatchmakingLoading(false);
        }
    };

    const recentActivity: ActivityItem[] = useMemo(() => {
        if (!stats?.recentReports) return [];
        return stats.recentReports.slice(0, 8).map(r => ({
            id: r.id,
            icon: <ReportIcon />,
            title: `${formatReportType(r.report_type)} report`,
            meta: `${r.reporter_email || 'Unknown'} → ${r.reported_user_email || 'Unknown'}`,
            timestamp: r.created_at,
            tone: r.priority === 'urgent' ? 'error'
                : r.priority === 'high' ? 'warning'
                : r.priority === 'low' ? 'info'
                : 'neutral',
            onClick: () => navigate(`/admin/reports/${r.id}`),
        }));
    }, [stats, navigate]);

    const attentionItems: AttentionItem[] = useMemo(() => {
        if (!stats) return [];
        const items: AttentionItem[] = [];
        if (stats.pendingReports > 0) {
            items.push({
                id: 'pending',
                label: 'Pending reports',
                helper: 'Reports waiting for moderator review',
                count: stats.pendingReports,
                tone: stats.pendingReports > 20 ? 'error' : 'warning',
                onClick: () => navigate('/admin/reports?status=pending'),
            });
        }
        if ((stats.pendingVideos ?? 0) > 0) {
            items.push({
                id: 'pending-videos',
                label: 'Intro videos to review',
                helper: 'Flagged by auto-moderation, awaiting approval',
                count: stats.pendingVideos ?? 0,
                tone: 'warning',
                onClick: () => navigate('/admin/videos'),
            });
        }
        if (stats.reportsByPriority?.urgent > 0) {
            items.push({
                id: 'urgent',
                label: 'Urgent priority',
                helper: 'High-severity reports flagged urgent',
                count: stats.reportsByPriority.urgent,
                tone: 'error',
                onClick: () => navigate('/admin/reports?priority=urgent'),
            });
        }
        if (stats.statusTrends?.suspensionsThisWeek) {
            items.push({
                id: 'suspensions',
                label: 'Suspensions this week',
                helper: 'Accounts paused after moderation review',
                count: stats.statusTrends.suspensionsThisWeek,
                tone: 'warning',
            });
        }
        if (stats.statusTrends?.bansThisWeek) {
            items.push({
                id: 'bans',
                label: 'Bans this week',
                helper: 'Accounts disabled; evidence retained',
                count: stats.statusTrends.bansThisWeek,
                tone: 'error',
            });
        }
        return items;
    }, [stats, navigate]);

    const totalSignupsLast30 = signupTrend.reduce((sum, r) => sum + r.signups, 0);

    if (error) {
        return (
            <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
                <AdminSidebar pendingReportsCount={stats?.pendingReports ?? 0} pendingVideosCount={stats?.pendingVideos ?? 0} />
                <Box component="main" sx={{ flexGrow: 1, p: `${T.space[6]}px`, minWidth: 0 }}>
                    <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={fetchDashboardStats}>Retry</Button>}
                        sx={{ maxWidth: 720, borderRadius: `${T.radius.md}px` }}
                    >
                        <Typography sx={{ fontWeight: T.type.weight.semibold }}>Failed to load dashboard</Typography>
                        <Typography variant="body2">{error}</Typography>
                    </Alert>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', bgcolor: T.color.surface.page, minHeight: '100vh' }}>
            <AdminSidebar pendingReportsCount={stats?.pendingReports ?? 0} pendingVideosCount={stats?.pendingVideos ?? 0} />
            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: `${T.space[4]}px`, md: `${T.space[6]}px` },
                minWidth: 0,
                overflow: 'hidden',
            }}>
                {/* Page header */}
                <Box sx={{ mb: `${T.space[6]}px`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: `${T.space[3]}px` }}>
                    <Box>
                        <Typography sx={{
                            fontSize: T.type.size['2xl'], fontWeight: T.type.weight.bold,
                            color: T.color.text.primary, lineHeight: T.type.lineHeight.tight,
                        }}>
                            Overview
                        </Typography>
                        <Typography sx={{ fontSize: T.type.size.md, color: T.color.text.secondary, mt: `${T.space[1]}px` }}>
                            Real-time signal on growth, engagement, and the moderation queue.
                        </Typography>
                    </Box>
                </Box>

                {/* KPI row */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="New signups (30d)"
                            value={totalSignupsLast30}
                            icon={<PersonAddIcon />}
                            tone="info"
                            loading={trendLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Reports today"
                            value={stats?.reportsToday}
                            icon={<ReportIcon />}
                            tone="warning"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Pending review"
                            value={stats?.pendingReports}
                            icon={<PendingIcon />}
                            tone={stats && stats.pendingReports > 20 ? 'error' : 'warning'}
                            loading={loading}
                            onClick={() => navigate('/admin/reports?status=pending')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Avg. response time"
                            value={stats?.avgResponseTimeHours}
                            format={(v) => `${v}h`}
                            icon={<ScheduleIcon />}
                            tone="neutral"
                            loading={loading}
                        />
                    </Grid>
                </Grid>

                {/* Secondary KPI row */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Resolved reports"
                            value={stats?.resolvedReports}
                            icon={<ResolvedIcon />}
                            tone="success"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Reports this week"
                            value={stats?.reportsThisWeek}
                            icon={<ReportIcon />}
                            tone="neutral"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Suspensions (7d)"
                            value={stats?.statusTrends?.suspensionsThisWeek}
                            icon={<BlockIcon />}
                            tone="warning"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Bans (7d)"
                            value={stats?.statusTrends?.bansThisWeek}
                            icon={<GavelIcon />}
                            tone="error"
                            loading={loading}
                        />
                    </Grid>
                </Grid>

                {/* Matchmaking KPIs */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Matches (30d)"
                            value={matchmaking?.totalMatches}
                            icon={<MatchIcon />}
                            tone="success"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Matches today"
                            value={matchmaking?.matchesToday}
                            icon={<TodayMatchIcon />}
                            tone="info"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Keep-rate (30d)"
                            value={matchmaking?.keepRate != null ? `${(matchmaking.keepRate * 100).toFixed(1)}%` : null}
                            icon={<KeepRateIcon />}
                            tone="neutral"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KPIStatCard
                            label="Decisions (30d)"
                            value={matchmaking?.totalDecisions}
                            icon={<DecisionsIcon />}
                            tone="neutral"
                            loading={matchmakingLoading}
                        />
                    </Grid>
                </Grid>

                {/* Trend chart + needs-attention */}
                <Grid container spacing={3} sx={{ mb: `${T.space[5]}px` }}>
                    <Grid item xs={12} lg={8}>
                        <TrendChart
                            title="Signups — last 30 days"
                            subtitle={signupSummary?.totalLast30 ? `${signupSummary.totalLast30.toLocaleString()} new accounts in the last 30 days` : undefined}
                            data={signupTrend}
                            series={[{ key: 'signups', label: 'Signups' }]}
                            loading={trendLoading}
                            empty={!trendLoading && signupTrend.length === 0}
                            emptyMessage="No signup trend data available yet."
                            height={300}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <NeedsAttentionPanel items={attentionItems} loading={loading} />
                    </Grid>
                </Grid>

                {/* Activity feed + top reported users */}
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                        <ActivityFeed
                            title="Recent activity"
                            items={recentActivity}
                            loading={loading}
                            emptyMessage="No recent reports."
                        />
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <Box sx={{
                            bgcolor: T.color.surface.raised,
                            border: `1px solid ${T.color.surface.border}`,
                            borderRadius: `${T.radius.lg}px`,
                            p: `${T.space[5]}px`,
                            boxShadow: T.shadow.sm,
                            height: '100%',
                        }}>
                            <Typography sx={{
                                fontSize: T.type.size.lg,
                                fontWeight: T.type.weight.semibold,
                                color: T.color.text.primary,
                                mb: `${T.space[4]}px`,
                            }}>
                                Top reported users
                            </Typography>

                            {loading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                    {[0, 1, 2, 3].map(i => (
                                        <Box key={i} sx={{
                                            height: 48, borderRadius: `${T.radius.md}px`,
                                            bgcolor: T.color.surface.sunken,
                                        }} />
                                    ))}
                                </Box>
                            ) : stats && stats.topReportedUsers.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${T.space[2]}px` }}>
                                    {stats.topReportedUsers.slice(0, 6).map((u, i) => (
                                        <Box
                                            key={u.user_id}
                                            onClick={() => navigate(`/admin/reports?reportedUserId=${u.user_id}`)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/reports?reportedUserId=${u.user_id}`); } }}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: `${T.space[3]}px`,
                                                p: `${T.space[3]}px`,
                                                borderRadius: `${T.radius.md}px`,
                                                bgcolor: T.color.surface.sunken,
                                                cursor: 'pointer',
                                                transition: `background-color ${T.motion.fast}`,
                                                '&:hover': { bgcolor: T.color.brand[50] },
                                                '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 2px ${T.color.brand[200]}` },
                                            }}
                                        >
                                            <Box sx={{
                                                width: 28, height: 28, borderRadius: '50%',
                                                bgcolor: T.color.brand[100], color: T.color.brand[600],
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: T.type.weight.semibold, fontSize: T.type.size.xs,
                                                flexShrink: 0,
                                            }}>
                                                {i + 1}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: T.type.size.sm,
                                                    fontWeight: T.type.weight.medium,
                                                    color: T.color.text.primary,
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {u.user_id.slice(0, 8)}…{u.user_id.slice(-4)}
                                                </Typography>
                                                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>
                                                    {u.report_count} {u.report_count === 1 ? 'report' : 'reports'}
                                                </Typography>
                                            </Box>
                                            <UserIcon sx={{ fontSize: 18, color: T.color.text.tertiary }} />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={{ py: `${T.space[5]}px`, textAlign: 'center', color: T.color.text.tertiary, fontSize: T.type.size.sm }}>
                                    No users have been reported.
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default AdminDashboard;
